<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <b-modal
      v-model="modal.show"
      :title="modalTitle"
      :ok-title="`Submit`"
      :ok-disabled="okButtonDisabled"
      @show="onShowModal"
      @hidden="onHideModal"
      @ok.prevent="handleSubmit(onSubmit)"
      @keydown.enter.prevent="handleSubmit(onSubmit)"
    >
      <content-spinner :show="loading" />
      <b-form v-if="!loading">
        <input-widget
          v-for="field in fields"
          :model="form"
          :field="field"
          :autofocus="field.autofocus"
        />
        <div class="page-toolbar bg-white shadow-sm p-3 justify-content-between align-items-center">
          <div class="d-flex justify-content-end">
            <b-button
              size="sm"
              class="ml-1 mr-3"
              @click="onAddNewBatchEndpointClick"
            >
              <font-awesome-icon :icon="['fas', 'plus']" />
              Add new Endpoint
            </b-button>
          </div>
        </div>
        <complex-table-view
          :is-file-manipulation-controller="true"
          :small="true"
          :fields="batchEndpointsTableFields"
          :items="form.batchEndpoints"
          :total-rows="form.batchEndpoints.length"
          :modal-name="`batch-endpoints-modal`"
          :action-buttons="batchEndpointsTableActions"
          @on-delete="onBatchEndpointDeleteClick"
        />
      </b-form>

      <add-edit-batch-endpoint-child-modal @submit="onAddNewEndpoint" />
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {v4 as uuidv4} from 'uuid';
import InputWidget from '../../../../core/components/complex-table/components/InputWidget';
import ContentSpinner from '../../../../core/components/ContentSpinner';
import ComplexTableView from '../../../../core/components/complex-table/components/ComplexTableView';
import AddEditBatchEndpointChildModal from './AddEditBatchEndpointChildModal';

const {mapState, mapActions} = createNamespacedHelpers('fileManipulationController');
const {mapState: mapConnectorState} = createNamespacedHelpers('connectorScreen');

export default {
  name: 'AddEditBatchEndpointModal',
  data() {
    return {
      loading: false,
      form: {
        batchName: null,
        batchEndpoints: [],
      },
      fields: {
        batchName: {
          type: 'text',
          required: true,
          label: 'Name',
          name: 'batchName',
        },
      },
      batchEndpointsTableFields: [
        {
          key: 'endpointName',
          name: 'endpointName',
          label: 'Name',
        },
      ],
      batchEndpointsTableActions: {
        edit: false,
        delete: true,
      },
    };
  },
  computed: {
    ...mapState({
      modal: state => state.addEditBatchEndpointModal,
    }),
    ...mapConnectorState({
      connectors: state => state.connectors,
      pitchBookEndpoints: state => state.startingPoint.pitchBookEndpoints,
    }),
    isEditMode() {
      return !!this.modal.data.batch;
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Batch' : 'Add New Batch';
    },
    okButtonDisabled() {
      return this.loading;
    },
  },
  methods: {
    ...mapActions([
      'showAddEditBatchEndpointChildModal',
      'hideAddEditBatchEndpointModal',
      'upsertConnectorBatch',
    ]),
    async onSubmit() {
      this.loading = true;

      const action = this.isEditMode ? 'update' : 'create';

      const {success, body} = await this.upsertConnectorBatch({
        action,
        data: this.form,
      });

      if (success) {
        this.$toasted.success(`Batch ${action}d successfully`, {
          duration: TOASTER_DURATION,
        });

        this.hideAddEditBatchEndpointModal();
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
      }

      this.loading = false;
    },
    onHideModal() {
      this.clearForm();
    },
    clearForm() {
      this.form.batchName = null;
      this.form.batchEndpoints = [];
    },
    onShowModal() {
      if (this.isEditMode) {
        this.form = {...this.modal.data.batch};
        this.setEndpointNameForBatchEndpoints();
      }
    },
    onBatchEndpointDeleteClick(index) {
      this.form.batchEndpoints = this.form.batchEndpoints.filter((item, idx) => idx !== index);
    },
    onAddNewBatchEndpointClick() {
      this.showAddEditBatchEndpointChildModal({
        batchEndpoints: this.form.batchEndpoints,
      });
    },
    getConnector(configurationId) {
      return this.connectors.find(connector => connector.configurationId === configurationId);
    },
    getEndpoint(connector, endpointId) {

      if (this.isPitchBook(connector)) {
        return Object.keys(connector.startingPoints).reduce((sp, startingPointId) => {
          const startingPoint = connector.startingPoints[startingPointId];
          if (startingPoint.startingPointId === endpointId) sp = startingPoint;
          return sp;
        }, {});
      }

      return connector.endpoints.find(connector => connector.endpointId === endpointId);
    },
    onAddNewEndpoint(formData) {
      const connector = this.getConnector(formData.configurationId);

      const endpointIds = [...formData.endpointId];
      const batchEndpoints = [];
      endpointIds.forEach(e => {
        const tmpData = {...formData};
        const endpoint = this.getEndpoint(connector, e.value);
        const endpointConfigurationJSON = endpoint.endpointConfigurationJSON ? JSON.parse(endpoint.endpointConfigurationJSON) : {};

        const tableName = this.isSnowflake(connector) ? endpointConfigurationJSON.tableName : null;
        const entityIndex = this.isPitchBook(connector) ? endpoint.entityName : null;
        const entityName = this.pitchBookEndpoints[entityIndex];

        if (!this.form.batchId) this.form.batchId = uuidv4();

        tmpData.endpointId = e.value;
        tmpData.batchId = this.form.batchId;
        tmpData.batchEndpointId = uuidv4();
        tmpData.endpointName = endpoint.friendlyName || entityName || tableName || endpoint.endpointId;

        batchEndpoints.unshift(tmpData);
      });

      this.form.batchEndpoints = [...batchEndpoints, ...this.form.batchEndpoints];
    },
    setEndpointNameForBatchEndpoints() {
      this.form.batchEndpoints = this.form.batchEndpoints.map(batchEndpoint => {
        const connector = this.getConnector(batchEndpoint.configurationId);
        const endpoint = this.getEndpoint(connector, batchEndpoint.endpointId);

        const endpointConfigurationJSON = endpoint.endpointConfigurationJSON ? JSON.parse(endpoint.endpointConfigurationJSON) : {};

        const tableName = this.isSnowflake(this.getConnector(batchEndpoint.configurationId)) ? endpointConfigurationJSON.tableName : null;
        const entityIndex = this.isPitchBook(connector) ? endpoint.entityName : null;
        const entityName = this.pitchBookEndpoints[entityIndex];

        return {...batchEndpoint, endpointName: endpoint.friendlyName || entityName || tableName || endpoint.endpointId};
      });
    },
    isSnowflake(connector) {
      return connector.vendor === 'Snowflake';
    },
    isPitchBook(connector) {
      return connector.vendor === 'PitchBook';
    },
  },
  components: {
    InputWidget,
    ContentSpinner,
    ComplexTableView,
    AddEditBatchEndpointChildModal,
  },
};
</script>

<style scoped>

</style>
